import React, { useState } from "react";
import { Container, Card, CardBody, Button, Input, InputGroup, InputGroupText, Spinner, Row, Col, FormGroup, Label, Badge } from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Logo from "../../Assets/images/logo.png";

const ForgotPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = useSelector(state => state.loading.loading);

    // Steps: 'email' -> 'otp' -> 'reset'
    const [step, setStep] = useState('email');
    const [form, setForm] = useState({ email: "", otp: "", password: "", confirm_password: "" });
    const [showPwd, setShowPwd] = useState(false);

    const onChange = e => setForm({ ...form, [e.target.name]: e.target.value });

    // Step 1: Send OTP
    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (!form.email) return toast.error("Email is required");

        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "POST", url: "/auth/send_otp.php", data: { email: form.email } });
            if (res?.success) {
                toast.success(res.message);
                setStep('otp');
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Failed to send OTP");
            }
        } catch (err) { toast.error("Network Error"); }
        finally { dispatch(loadingFalse()); }
    };

    // Step 2: Verify OTP
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (form.otp.length !== 6) return toast.error("Enter valid 6-digit OTP");

        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "POST", url: "/auth/verify.php", data: { email: form.email, otp: form.otp } });
            if (res?.success) {
                toast.success(res.message);
                setStep('reset');
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Invalid OTP");
            }
        } catch (err) { toast.error("Verification failed"); }
        finally { dispatch(loadingFalse()); }
    };

    // Step 3: Reset Password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirm_password) return toast.error("Passwords do not match");
        if (form.password.length < 8) return toast.error("Password must be at least 8 characters");

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/auth/reset_password.php",
                data: { email: form.email, password: form.password, confirm_password: form.confirm_password }
            });
            if (res?.success) {
                toast.success("Password reset successfully! Please login.");
                navigate("/login");
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Reset failed");
            }
        } catch (err) { toast.error("Error resetting password"); }
        finally { dispatch(loadingFalse()); }
    };

    return (
        <div className="login-wrapper d-flex align-items-center justify-content-center py-4"
            style={{ minHeight: '100vh', backgroundColor: '#f4f7fe' }}>
            <Container>
                <Row className="justify-content-center g-4">
                    <Col xs={12} sm={10} md={8} lg={5} xl={4}>
                        <div className="text-center mb-4">
                            <img src={Logo} alt="Logo" style={{ maxHeight: '80px', marginBottom: '20px' }} />
                            <h3 className="fw-bold" style={{ color: '#1B2559' }}>Account Recovery</h3>
                            <p className="text-muted small">Follow the steps to reset your password</p>
                        </div>

                        <Card className="border-0 shadow-lg" style={{ borderRadius: '24px' }}>
                            <CardBody className="p-4 p-md-5">

                                {step === 'email' && (
                                    <form onSubmit={handleSendOtp}>
                                        <FormGroup className="mb-4">
                                            <Label className="small fw-bold text-dark">Registered Email Address</Label>
                                            <Input type="email" name="email" placeholder="Enter your email"
                                                value={form.email} onChange={onChange} required
                                                style={{ borderRadius: '12px', padding: '12px' }} />
                                        </FormGroup>
                                        <Button block type="submit" disabled={loading} className="py-3 border-0"
                                            style={{ backgroundColor: '#4318FF', borderRadius: '16px', fontWeight: '700' }}>
                                            {loading ? <Spinner size="sm" /> : "Send OTP"}
                                        </Button>
                                    </form>
                                )}

                                {step === 'otp' && (
                                    <form onSubmit={handleVerifyOtp}>
                                        <div className="text-center mb-3">
                                            <Badge color="soft-primary" className="text-primary mb-2">OTP sent to {form.email}</Badge>
                                        </div>
                                        <FormGroup className="mb-4">
                                            <Label className="small fw-bold text-dark">Enter 6-Digit OTP</Label>
                                            <Input type="text" name="otp" placeholder="000000" maxLength="6"
                                                className="text-center fw-bold fs-4"
                                                value={form.otp} onChange={onChange} required
                                                style={{ borderRadius: '12px', letterSpacing: '8px' }} />
                                        </FormGroup>
                                        <Button block type="submit" disabled={loading} className="py-3 border-0"
                                            style={{ backgroundColor: '#2B3674', borderRadius: '16px', fontWeight: '700' }}>
                                            {loading ? <Spinner size="sm" /> : "Verify OTP"}
                                        </Button>
                                        <div className="text-center mt-3">
                                            <Button color="link" size="sm" className="text-decoration-none" onClick={() => setStep('email')}>Change Email</Button>
                                        </div>
                                    </form>
                                )}

                                {step === 'reset' && (
                                    <form onSubmit={handleResetPassword}>
                                        <FormGroup className="mb-3">
                                            <Label className="small fw-bold text-dark">New Password</Label>
                                            <InputGroup>
                                                <Input type={showPwd ? "text" : "password"} name="password" placeholder="Min 8 characters"
                                                    value={form.password} onChange={onChange} required
                                                    style={{ borderRadius: '12px 0 0 12px', padding: '12px' }} />
                                                <InputGroupText className="bg-white" onClick={() => setShowPwd(!showPwd)} style={{ cursor: 'pointer', borderRadius: '0 12px 12px 0' }}>
                                                    <i className={`bx ${showPwd ? "bx-show" : "bx-hide"}`}></i>
                                                </InputGroupText>
                                            </InputGroup>
                                        </FormGroup>
                                        <FormGroup className="mb-4">
                                            <Label className="small fw-bold text-dark">Confirm New Password</Label>
                                            <Input type="password" name="confirm_password" placeholder="Re-enter password"
                                                value={form.confirm_password} onChange={onChange} required
                                                style={{ borderRadius: '12px', padding: '12px' }} />
                                        </FormGroup>
                                        <Button block type="submit" disabled={loading} className="py-3 border-0"
                                            style={{ backgroundColor: '#05CD99', borderRadius: '16px', fontWeight: '700' }}>
                                            {loading ? <Spinner size="sm" /> : "Update Password"}
                                        </Button>
                                    </form>
                                )}

                                <div className="text-center mt-4">
                                    <Button color="link" className="small text-decoration-none fw-bold"
                                        style={{ color: '#4318FF' }} onClick={() => navigate("/login")}>
                                        Back to Login
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ForgotPassword;